/**
 * BuildReadinessChecklist — compact pre-build readiness indicator (NC-983 / F1).
 *
 * Shows, before an operator dispatches a 60–90 min image build, which build
 * essentials are satisfied and which are still open. Purely presentational —
 * the evaluation lives in utils/workloadReadiness so it can be unit-tested and
 * reused by the NC-972 wizard.
 */

import React from 'react';
import {
  Alert,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as OpenIcon,
} from '@mui/icons-material';
import { ReadinessCheck } from '../../utils/workloadReadiness';

interface BuildReadinessChecklistProps {
  checks: ReadinessCheck[];
  allReady: boolean;
}

export default function BuildReadinessChecklist({
  checks,
  allReady,
}: BuildReadinessChecklistProps) {
  return (
    <Box sx={{ mt: 2 }} data-testid="build-readiness-checklist">
      <Typography variant="subtitle2" gutterBottom>
        Build-Bereitschaft
      </Typography>

      <List dense disablePadding>
        {checks.map((check) => (
          <ListItem key={check.key} disableGutters>
            <ListItemIcon sx={{ minWidth: 32 }}>
              {check.ok ? (
                <CheckCircleIcon color="success" fontSize="small" />
              ) : (
                <OpenIcon color="warning" fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={check.label}
              secondary={check.ok ? undefined : check.hint}
            />
          </ListItem>
        ))}
      </List>

      {allReady ? (
        <Alert severity="success" sx={{ mt: 1 }}>
          Alle Voraussetzungen erfüllt — der Build kann gestartet werden.
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Es fehlen noch Angaben. Du kannst trotzdem bauen, aber der Build schlägt
          wahrscheinlich fehl (Dauer 60–90 min / Kosten €5–10). Ergänze die oben
          markierten Punkte zuerst.
        </Alert>
      )}
    </Box>
  );
}
